import { Injectable, NgZone, signal, WritableSignal } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import olMap from 'ol/Map';
import VectorSource from 'ol/source/Vector';
import { Vector as VectorLayer } from 'ol/layer';
import * as olControl from 'ol/control';
// import Control from 'ol/control/Control';
// import * as olEvents from 'ol/events';
import * as olInteraction from 'ol/interaction';
import Interaction from 'ol/interaction/Interaction';
import Attribution from 'ol/control/Attribution';

// import { DragRotateAndZoom, defaults as defaultInteractions, } from 'ol/interaction';
// import PinchRotate from 'ol/interaction/PinchRotate';
// import * as olCondition from 'ol/events/condition';
import { Renderer2 } from '@angular/core';
import { RendererFactory2 } from '@angular/core';
// import ResizeObserver from 'resize-observer-polyfill';
import { Collection, Feature, Kinetic, MapBrowserEvent } from 'ol';
import * as olCoordinate from 'ol/coordinate';
import { EventsKey } from 'ol/events';
import { unByKey } from 'ol/Observable';
import { Pixel } from 'ol/pixel';
import { ToggleEventNameType, ToggleEventService } from '@tt-lib/services/toggle-event.service';
import { BrowserSnifferService } from './browser-sniffer.service';
import { touchOnly } from 'ol/events/condition';
import { Capacitor } from '@capacitor/core';


export interface TTPointerEvent extends PointerEvent {
  splitMapEvent: boolean;
}
export type TTPointerMapBrowserEvent = MapBrowserEvent<TTPointerEvent>;

@Injectable({
  providedIn: 'root'
})
export class MapService {

  //private _map: olMap;
  public _maps = new Map<string, olMap>();
  private mapReadySubject = new BehaviorSubject<string>('');
  private mapReady$: Observable<any>;
  public mapClickOnMarker$: Observable<any>;
  public olMapClickListener: EventsKey;
  public olSplitMapClickListener: EventsKey;
  private pointerCursorOnFeatureHoverlayers: string[] = [];
  // private sizeObserver: ResizeObserver;
  private mapElement: HTMLElement;
  public resize$ = new BehaviorSubject<[number, number]>([0, 0]);

  private searchUrl: string;

  /**
   * this setting should be used by all services and components when searching for features on the map, e.g. trace service & remark component
   */
  public readonly searchPixelTolerance: number = 15;

  private renderer: Renderer2;


  // Create this ONCE
  readonly sharedDrawSource = new VectorSource({
    //format: new GPX()
  });

  /**
   * for touch devices: this value becomes false on 'pointerdown' and becomes true again if the user keeps 
   * holding minimal x milliseconds before triggering the 'pointerup' event; if a pointer drag event happens 
   * within those x milliseconds, the value will not become true
   * for non touch devices: if a rightclick event happens, this value becomes true
   */
  public wasContextMenuEvent: WritableSignal<boolean> = signal(false);
  /** 
   * when the property wasContextMenuEvent becomes true, the geolocation component will claim this event x milliseconds after it happened
   * if no other component or service claimed this event, the geolocation component will claim it and show a popup
  */
  public contextMenuEventClaimed: boolean = false;
  public contextMenuCoordinate: olCoordinate.Coordinate = null;
  public contextMenuMapId: string = null;
  private contextMenuTapHoldStart: number = -1;
  public contextMenuTapHoldEnd: number = -1;
  private contextMenuTapHoldTimer: number = -1;
  public wasContextMenuEventAfterRightClick: boolean = false;
  public wasContextMenuEventAfterTouch: boolean = false;
  public readonly longTapInMilliSeconds: number = 1500; //swipe componentcancels the long tap 
  private _contextMenuPaused: WritableSignal<{ paused: boolean, pausedBy: string }> = signal({ paused: false, pausedBy: '' });
  public readonly contextMenuPausedRef = this._contextMenuPaused.asReadonly();

  /**
   * the backEventService will update this variable during activation
  */
  public backEventServiceActive: boolean = false;

  /**
   * this value becomes true if the url contains at startup one of these params; xy , zoom , tracezoom , tracetab (see MapComponent > initParams)
   * this value is used by the geolocation component at initialization, to determine if it can zoom to the user's location or not
   */
  public wasStartUpWithPositionOrZoomRelatedParams: boolean = false;
  sizeObserver: ResizeObserver;

  constructor(
    private rendererFactory: RendererFactory2,
    private zone: NgZone,
    private toggleEventService: ToggleEventService,
    private browserSnifferService: BrowserSnifferService
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
    this.mapReady$ = this.mapReadySubject.asObservable();
  }

  initMapClickOnMarker(mappi) {
    //returns marker feature or null on mapclick
    this.mapClickOnMarker$ =
      new Observable((observer) => {
        this.olMapClickListener = mappi.on("click", (event: PointerEvent) => {
          observer.next(event);
        })
      }
      ).pipe(
        map((event: MapBrowserEvent<PointerEvent>) => {
          var pixelClick = (event.type === 'touchstart') ? mappi.getEventPixel(event) : event.pixel;
          return (
            of(mappi.forEachFeatureAtPixel(pixelClick, (markerFeature: Feature, layer) => {
              if (this.isTTPointerMapBrowserEvent(event)
                && event.originalEvent.splitMapEvent
                && !markerFeature.get('highlight')
                && this.contextMenuMapId !== 'splitviewMap') {
                //popup should appear on the splitviewMap
                this.contextMenuMapId = 'splitviewMap';
              }
              return markerFeature
            },
              {
                hitTolerance: this.searchPixelTolerance,
                layerFilter: (layer) => layer.get('customId') === 'markerLayer' || layer.get('customId') === 'markerLayerBlend'
              }
            ) || null)
          )
        }),
        switchMap(marker => { return marker })
      );
  }

  initSplitMapClickOnMarker(splitmap) {
    this.olSplitMapClickListener = splitmap.on('click', (evt) => {

      let forwardClick = false;

      if (this.olMapClickListener) {

        let otherLayerFeatureCount = 0;

        //if we find any feature from another layer than markerLayer, than we don't forward the click
        //when active, mapindex layer is everywhere, so ignore it
        splitmap.forEachFeatureAtPixel(evt.pixel, (feature, layer) => {
          if (typeof layer.get('customId') === 'string') {
            otherLayerFeatureCount += layer.get('customId') === 'markerLayer_splitviewMap' || layer.get('customId').indexOf('mapindex') > -1 ? 0 : 1;
          }
        }, {
          hitTolerance: this.searchPixelTolerance,
        });

        forwardClick = otherLayerFeatureCount === 0;
      }

      if (forwardClick) {
        const coordinate = evt.coordinate;
        const targetPixel = this.getMap().getPixelFromCoordinate(coordinate);

        if (targetPixel) {
          const simulatedEvent = new MapBrowserEvent('click', this.getMap(), {
            clientX: targetPixel[0],
            clientY: targetPixel[1],
            type: 'click',
            splitMapEvent: true
          } as TTPointerEvent);

          simulatedEvent.coordinate = coordinate;
          simulatedEvent.pixel = targetPixel;
          this.getMap().dispatchEvent(simulatedEvent);
        }
      }
    });
  }

  removeSplitMapClickOnMarker() {
    unByKey(this.olSplitMapClickListener);
    this.olSplitMapClickListener = null;
  }

  isTTPointerMapBrowserEvent(
    event: MapBrowserEvent<any>
  ): event is TTPointerMapBrowserEvent {
    // Check if originalEvent exists, is a native PointerEvent, and has your custom field
    return (
      event.originalEvent !== undefined &&
      event.originalEvent instanceof PointerEvent &&
      'splitMapEvent' in event.originalEvent
    );
  }

  initResizeObserver(mapElement) {
    //!resize observer test
    this.mapElement = document.querySelector('#map')
    // const map = new Map({
    //   target: mapElement
    // })
    //? emit updatesize event?
    this.sizeObserver = new ResizeObserver(entries => {
      const width: number = entries[0].contentRect.width;
      const height: number = entries[0].contentRect.height;
      this.zone.run(() => {
        this.resize$.next([width, height]);
      });

    });
    // this.sizeObserver = new ResizeObserver(() => {
    //   this.map.updateSize()
    // })
    this.sizeObserver.observe(mapElement);
    //!
    //sizeObserver.unobserve(myEl);

  }

  // private getMarkerClick$(event, map: Map): Observable<any> {
  //   var pixelClick = (event.type === 'touchstart') ? map.getEventPixel(event) : event.pixel;
  //   let marker: Observable<any> = of(
  //     map.forEachFeatureAtPixel(pixelClick, (feature) => feature,
  //       { layerFilter: (layer) => layer.get('customId') === 'markerLayer' }
  //     ) || null
  //   )
  //   return marker;
  // }

  /**
  * create a map with getMap
  */
  private createMap(targetId: string, controls?: Collection<olControl.Control>, interactions?: Interaction): olMap {

    let attribution = new Attribution({
      collapsible: false
    });

    // this._map = new Map({
    const _map = new olMap({
      target: targetId,
      //keyboardEventTarget: targetId,
      keyboardEventTarget: document,
      controls: controls ? controls : olControl.defaults({
        zoom: false,
        //pinchRotate: true,
        rotateOptions: {
          autoHide: true,
          label: this.renderer.createElement('ngi-compass'),
          compassClassName: 'ngi-compass'
        }
      }).extend([attribution]),
      //layers: [],
      interactions: olInteraction.defaults({
        altShiftDragRotate: false,
        pinchRotate: true,
        keyboard: true,
        dragPan: false
      }).extend([
        //new PinchRotate(),
        //new DragRotateAndZoom()
        new olInteraction.DragPan({
          /* 
          when the backEventService is active, we want avoid conflict between map pan event and screen border gesture,
          more specifically for touch devices, native apps
          especially on ios this is a problem (android prioritizes the border back swipe)
          */
          kinetic: new Kinetic(-0.005, 0.05, 100), 
          condition: (event: MapBrowserEvent) => { 
            return !touchOnly(event) 
            || !this.backEventServiceActive 
            /* || this.browserSnifferService.isPhone() */ 
            /* || !Capacitor.isNativePlatform() */
            || !this.isTapOnBorder(_map.getEventPixel(event.originalEvent), _map, 'cancelpandrag'); }
        })
      ]),
    });

    let drawLayer = new VectorLayer({
      zIndex: 100,
      source: this.sharedDrawSource,
    })
    drawLayer.set('customId', 'drawLayer')
    _map.addLayer(drawLayer)
    return _map;
  }



  setMapReady(id: string) {
    const key = id ?? 'main'; this.mapReadySubject.next(key);
    //this.mapReadySubject.next(id);
  }
  getMapReady$(id?: string): Observable<boolean> {
    const key = id ?? 'main';
    return this.mapReady$.pipe(filter(readyId => readyId === key), map(() => true), take(1));
  }
  // setCurrentMap(map: olMap): void {
  //   this._map = map;
  // }

  getCurrentMap(id?): olMap {
    //return this._map;
    if (!id) {
      return this._maps.get('main');
    }
    else {
      //return this._maps.get('id');
      return this._maps.get(id);
    }
  }

  //creates map if not existing
  getMap(id?: string, controls?: Collection<olControl.Control>, interactions?: Interaction): olMap {
    if (!id) {
      if (!this._maps.has('main')) {
        this._maps.set('main', this.createMap("map", controls, interactions))
      }
      return this._maps.get('main');
    }
    else {
      if (!this._maps.has(id)) {
        this._maps.set(id, this.createMap(id, controls, interactions))
      }
      return this._maps.get(id);
    }
  }

  //loading=false;
  // TODO: use the adapter pattern
  // ? create a separate service for the geocoding ? 
  // !moved to viewservice
  // search(inputUrl: string): Observable<any> {
  //   console.log('search...')
  //   console.log(inputUrl)
  //   return this.http.get<any>(inputUrl)
  //     .pipe(map(res => res.features));
  // }

  setPointerCursorOnFeatureHover(map?: olMap) {
    const monitoredMap = map ? map : this.getMap();
    monitoredMap.on('pointermove', (evt) => {
      if (evt.dragging) {
        return;
      }
      var pixel = monitoredMap.getEventPixel(evt.originalEvent);

      //case layer with getFeatureInfo
      let hitFeature = monitoredMap.forEachFeatureAtPixel(pixel, (feature, layer) => {
        return true;
      },
        {
          layerFilter: (layer) => {
            //return (layer.get('customId') === 'markerLayer')//customId);
            // return ['markerLayer', 'zoneLayer'].includes(layer.get('customId'))//customId);
            return this.pointerCursorOnFeatureHoverlayers.includes(layer.get('customId'))//customId);
          }
        }
      );
      monitoredMap.getTargetElement().style.cursor = hitFeature ? 'pointer' : '';
    });
  }

  setPointerCursorOnFeatureHoverForLayerId(layerId) {
    if (!this.pointerCursorOnFeatureHoverlayers.includes(layerId)) {
      this.pointerCursorOnFeatureHoverlayers.push(layerId);
    }
  }

  removePointerCursorOnFeatureHoverForLayerId(layerId) {
    let foundIndex = this.pointerCursorOnFeatureHoverlayers.indexOf(layerId)
    if (foundIndex > -1) {
      this.pointerCursorOnFeatureHoverlayers.splice(foundIndex, 1);
    }
  }

  public handleTapHoldStart(event: PointerEvent, mapId: string) {
    if (this.contextMenuTapHoldStart === -1 && !this._contextMenuPaused().paused) {

      this.contextMenuMapId = null;
      this.contextMenuCoordinate = null;
      this.contextMenuEventClaimed = false;
      this.wasContextMenuEventAfterTouch = event.pointerType === "touch";
      this.wasContextMenuEventAfterRightClick = false;

      const map = this.getMap(mapId);
      const pixel = map.getEventPixel(event);

      //on touch, we don't allow on border, to avoid back swipe to trigger a popup
      //if the center swipe component is activated, also check if it was not a click on that div
      if (!this.isTapOnOlSwipeElement(event) && (!this.wasContextMenuEventAfterTouch || !this.isTapOnBorder(pixel, map, 'cancelcontextmenu'))) {

        const coordinate = map && event ? map.getCoordinateFromPixel(pixel) : null;

        if (coordinate) {

          this.contextMenuTapHoldEnd = -1;
          const mapCenter = map.getView().getCenter();
          const startTimestamp = Date.now();
          this.contextMenuTapHoldStart = startTimestamp;

          this.contextMenuMapId = mapId;
          this.contextMenuCoordinate = coordinate;

          this.wasContextMenuEvent.set(false);

          //no long tap event for mouse click
          this.contextMenuTapHoldTimer = this.wasContextMenuEventAfterTouch ? window.setTimeout(() => {
            this.contextMenuTapHoldTimer = -1

            //there was no pointerdrag? we allow 5 pixels movement
            const oldCenterPixel = map.getPixelFromCoordinate(mapCenter);
            const newCenterPixel = map.getPixelFromCoordinate(map.getView().getCenter());
            const deltaX = newCenterPixel[0] - oldCenterPixel[0];
            const deltaY = newCenterPixel[1] - oldCenterPixel[1];
            const pixelDistance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

            if (this.contextMenuTapHoldStart === startTimestamp && pixelDistance <= 5 && !this._contextMenuPaused().paused) {
              this.contextMenuEventClaimed = false;
              this.wasContextMenuEvent.set(true);
              console.log("long tap event");
            }
          }, this.longTapInMilliSeconds) : -1;
        }
      }
    }
  }
  public handleTapHoldEnd() {
    //popup should not close and marker highlight should not be removed on long tap end 
    //tapHoldEndTimestamp is checked in marker & popop services
    this.contextMenuTapHoldStart = -1;

    if (this.contextMenuTapHoldTimer !== -1) {
      window.clearTimeout(this.contextMenuTapHoldTimer);
      this.contextMenuTapHoldTimer = -1;
      //console.log("long tap event cancelled");
    }

    this.contextMenuTapHoldEnd = Date.now();
  }

  public handleContextMenu(event: PointerEvent) {
    //no context for touch, only tap and hold
    //this.wasContextMenuEventAfterTouch is normally already false
    if (event.pointerType === "touch" || this._contextMenuPaused().paused) {
      return;
    }

    this.wasContextMenuEventAfterRightClick = true;

    //normally already handled by handleTapHoldStart
    this.wasContextMenuEventAfterTouch = false;
    //normally already handled by handleTapHoldEnd
    if (this.contextMenuTapHoldTimer !== -1) {
      window.clearTimeout(this.contextMenuTapHoldTimer);
      this.contextMenuTapHoldTimer = -1;
    }
    //normally already handled by handleTapHoldEnd
    this.contextMenuTapHoldStart = -1;
    //normally already handled by handleTapHoldEnd
    this.contextMenuTapHoldEnd = Date.now();

    this.contextMenuEventClaimed = false;
    this.wasContextMenuEvent.set(true);
  }

  public set contextMenuPaused(pausedInfoObj: { paused: boolean, pausedBy: string }) {
    pausedInfoObj = typeof pausedInfoObj === 'object' ? pausedInfoObj : { paused: false, pausedBy: '' };
    pausedInfoObj.paused = typeof pausedInfoObj.paused == 'boolean' ? pausedInfoObj.paused : false;
    pausedInfoObj.pausedBy = typeof pausedInfoObj.pausedBy == 'string' ? pausedInfoObj.pausedBy : '';
    this._contextMenuPaused.set(pausedInfoObj);
    console.log(`context menu is ${this._contextMenuPaused().paused ? 'paused' : 'activated again'} , this was requested by ${this._contextMenuPaused().pausedBy} .`)
  }

  public hasActiveContextMenuTapHoldTimer() {
    return this.contextMenuTapHoldTimer !== -1;
  }

  /**
   * this method should normally only be used on touch devices; the method who calls isTapOnBorder should do that test before calling this method
   * 
   * @param pixel 
   * @param map 
   * @param checkType
   * @returns 
   */
  private isTapOnBorder(pixel: Pixel, map: olMap, checkType: 'cancelcontextmenu' | 'cancelpandrag') {
    const [clickX, clickY] = pixel;
    const [mapWidth, mapHeight] = map.getSize();
    //swipe left/right
    const thresholdLeftRight = checkType === 'cancelpandrag' ? 3 : 10;
    const thresholdTop = checkType === 'cancelpandrag' ? 0 : 10;
    //swipe up
    //when swiping away the app, using the boottom bar, we want to avoid that the context menu still appears afterwards, the bottom bar height is average 50
    const thresholdBottom = checkType === 'cancelpandrag' ? 0 : 50;

    const isAtBorder =
      clickX < thresholdLeftRight ||
      clickY < thresholdTop ||
      (mapWidth - clickX) < thresholdLeftRight ||
      (mapHeight - clickY) < thresholdBottom;

    if (isAtBorder && checkType === 'cancelcontextmenu') {
      console.log('tap/click on border pixels will be ignored for long tap event');
    }

    return isAtBorder;
  }

  private isTapOnOlSwipeElement(event: PointerEvent) {
    return event
      && event.target
      && event.target instanceof Element
      && (event.target.classList.contains('ol-swipe') || (event.target.parentElement && event.target.parentElement.classList.contains('ol-swipe')));
  }

  public handleAppVisibilityChange(isVisible: boolean) {
    if (!isVisible) {
      //app hidden
      this.toggleEventService.toggleEvent({ forwardedFor: "MapService", eventName: ToggleEventNameType.INVISIBLE });

      /*
      sometimes a swipe up event trigger the long tap event, though it should be blocked by the screen border check (isTapOnBorder)
      we try to cancel the popup if the timer was already started
      */
      this.contextMenuEventClaimed = true;
      this.contextMenuTapHoldStart = -1;
      if (this.hasActiveContextMenuTapHoldTimer()) {
        //console.log("cancelled long tap event via MapService handleAppVisibilityChange");
        this.handleTapHoldEnd();
      } else {
        //check again, maybe method handleTapHoldStart didn't initialize the timer yet
        setTimeout(() => {
          this.contextMenuEventClaimed = true;
          this.contextMenuTapHoldStart = -1;
          if (this.hasActiveContextMenuTapHoldTimer()) {
            //console.log("cancelled long tap event via MapService handleAppVisibilityChange");
            this.handleTapHoldEnd();
          }
        }, 500);
      }
    } else {
      //app visible
      this.toggleEventService.toggleEvent({ forwardedFor: "MapService", eventName: ToggleEventNameType.VISIBLE });
    }
  }
}